import axios, { AxiosResponse } from 'axios';
import { Address, Client, PersonOfInterest, ApiResponse, Quote, CreateQuoteRequest } from '../types/index';

const API_URL = process.env.REACT_APP_API_URL;

export const getUser = async (token: string): Promise<any> => {
  const response = await axios.get(`${API_URL}/auth/me`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  //console.log('API Response:', response.data);
  return response.data;
};

export const getClient = async (clientId: string, token: string): Promise<Client> => {
  const response = await axios.get(`${API_URL}/api/clients/${clientId}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  //console.log('Client API Response:', response.data);
  return response.data;
};

export const getAddresses = async (clientId: string, token: string): Promise<Address[]> => {
  const response = await axios.get(`${API_URL}/api/addresses?clientId=${encodeURIComponent(clientId)}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  //console.log('Fetched addresses:', response.data);
  return response.data;
};

export const addAddress = async (address: Address & { clientId: number }, token: string): Promise<Address> => {
  const response = await axios.post(`${API_URL}/addresses`, address, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data;
};

export const updateAddress = async (addressId: number, address: Address & { clientId: number }, token: string): Promise<Address> => {
  const response = await axios.put(`${API_URL}/api/addresses/${addressId}`, address, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data;
};

export const getPersonsOfInterest = async (clientId: string, token: string): Promise<ApiResponse<PersonOfInterest[]>> => {
  try {
    const response: AxiosResponse<PersonOfInterest[]> = await axios.get(
  `${API_URL}/api/persons-of-interest?clientId=${encodeURIComponent(clientId)}`,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    //console.log('Fetched persons of interest:', response.data);
    return {
      success: true,
      data: Array.isArray(response.data) ? response.data.filter((item): item is PersonOfInterest => item !== null) : [],
      message: '',
    };
  } catch (err: any) {
    return {
      success: false,
      data: [],
      message: err.response?.data?.message || 'Failed to fetch persons of interest',
    };
  }
};

export const addPersonOfInterest = async (
  person: PersonOfInterest & { clientId: number },
  token: string
): Promise<ApiResponse<PersonOfInterest>> => {
  try {
    const response: AxiosResponse<PersonOfInterest> = await axios.post(`${API_URL}/api/persons-of-interest`, person, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return {
      success: true,
      data: response.data,
      message: '',
    };
  } catch (err: any) {
    return {
      success: false,
      data: null,
      message: err.response?.data?.message || 'Failed to add person',
    };
  }
};

export const updatePersonOfInterest = async (
  personId: number,
  person: PersonOfInterest & { clientId: number },
  token: string
): Promise<ApiResponse<PersonOfInterest>> => {
  try {
    const response: AxiosResponse<PersonOfInterest> = await axios.put(
  `${API_URL}/api/persons-of-interest/${personId}`,
      person,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    return {
      success: true,
      data: response.data,
      message: '',
    };
  } catch (err: any) {
    return {
      success: false,
      data: null,
      message: err.response?.data?.message || 'Failed to update person',
    };
  }
};

export const updateClient = async (clientId: string, clientToSend: Partial<Client>, token: string): Promise<Client> => {
  try {
    const response = await axios.put<Client>(
  `${API_URL}/api/clients/${clientId}`,
      clientToSend,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    //console.log('Updated client API Response:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('Error updating client:', {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
      headers: error.response?.headers,
    });
    throw new Error(error.response?.data?.message || 'Failed to update client');
  }
};

export const api = {
  createQuote: async (quoteData: CreateQuoteRequest): Promise<ApiResponse<Quote>> => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        return {
          success: false,
          message: 'Authentication token not found',
          data: null as any,
        };
      }

      console.log('🚀 Sending quote data to backend:', {
        administrator_ids: quoteData.administrator_ids,  // Use plural (array)
        underwriter_ids: quoteData.underwriter_ids,      // Use plural (array)
        fullData: quoteData
      });

      const response = await axios.post(`${API_URL}/api/quotes`, quoteData, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      console.log('✅ Backend response:', response.data);
      
      return response.data;
    } catch (err: any) {
      console.error('❌ API call failed:', err);
      
      if (err.response) {
        console.error('Backend error response:', err.response.data);
        return {
          success: false,
          message: err.response.data?.message || `Server error: ${err.response.status}`,
          data: null as any,
        };
      } else if (err.request) {
        return {
          success: false,
          message: 'No response from server',
          data: null as any,
        };
      } else {
        return {
          success: false,
          message: err.message || 'Unknown error occurred',
          data: null as any,
        };
      }
    }
  },

  getQuotesByClient: async (clientId: number): Promise<ApiResponse<Quote[]>> => {
    try {
      const response = await fetch(`${API_URL}/api/quotes/client/${clientId}`);
      
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const textResponse = await response.text();
        console.error('Non-JSON response:', textResponse);
        
        return {
          success: false,
          message: `Server error: ${response.status} ${response.statusText}`,
          data: [],
        };
      }

      const data = await response.json();
      
      if (!response.ok) {
        return {
          success: false,
          message: data.message || `Error: ${response.status} ${response.statusText}`,
          data: [],
        };
      }
      
      return data;
    } catch (err: any) {
      console.error('API call failed:', err);
      return {
        success: false,
        message: err.message || 'Network error occurred',
        data: [],
      };
    }
  },
};